import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

export interface BillingInfo {
  restaurant_id: number
  business_name: string
  legal_id: string
  legal_id_type: string
  address: string | null
  phone: string | null
  email: string | null
  regime: string
  footer_note: string | null
  current_consecutive: number
}

export interface PaymentItemSnapshot {
  order_item_id: string
  item_name: string
  quantity: number
  unit_price: number
  line_total: number
  notes?: string | null
}

export interface ProcessPaymentInput {
  restaurantId: number | string
  orderId: string
  tableId?: string | null
  amount: number
  method: 'cash' | 'sinpe' | 'card'
  items: PaymentItemSnapshot[]
  isPartial: boolean
  createdBy?: string | null
}

export interface PaymentRecord {
  id: string
  restaurant_id: number
  order_id: string
  amount: number
  method: string
  items_json: PaymentItemSnapshot[]
  consecutive_number: number
  is_partial: boolean
  created_at: string
}

// ─── Hook: load billing info for a restaurant ─────────────────────────────
export function useBillingInfo(restaurantId: number | string | undefined) {
  const [info, setInfo] = useState<BillingInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchInfo = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('restaurant_billing_info')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .maybeSingle()
      if (error) throw error
      setInfo((data as BillingInfo) || null)
    } catch (err) {
      console.error('[NOIZA] Error loading billing info:', err)
      setInfo(null)
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchInfo()
  }, [fetchInfo])

  return { info, loading, refetch: fetchInfo }
}

// ─── Process a payment (full or partial) ──────────────────────────────────
export async function processPayment(
  input: ProcessPaymentInput,
): Promise<{ success: boolean; payment?: PaymentRecord; error?: string }> {
  try {
    // 1) Reserve consecutive number atomically
    const { data: consecutiveData, error: consecErr } = await supabase.rpc(
      'increment_billing_consecutive',
      { p_restaurant_id: Number(input.restaurantId) },
    )
    if (consecErr) throw consecErr
    const consecutive = consecutiveData as number

    // 2) Insert payment row
    const { data: payment, error: payErr } = await supabase
      .from('payments')
      .insert({
        restaurant_id: Number(input.restaurantId),
        order_id: input.orderId,
        table_id: input.tableId || null,
        amount: input.amount,
        method: input.method,
        items_json: input.items,
        item_count: input.items.reduce((s, i) => s + i.quantity, 0),
        consecutive_number: consecutive,
        is_partial: input.isPartial,
        created_by: input.createdBy || null,
      })
      .select('*')
      .single()
    if (payErr) throw payErr

    // 3) Mark order_items as paid
    const itemIds = input.items.map((i) => i.order_item_id)
    if (itemIds.length > 0) {
      const { error: updErr } = await supabase
        .from('order_items')
        .update({
          paid_at: new Date().toISOString(),
          paid_in_payment_id: payment.id,
        })
        .in('id', itemIds)
      if (updErr) throw updErr
    }

    // 4) If full payment (not partial) → close the order
    if (!input.isPartial) {
      await supabase
        .from('orders')
        .update({
          status: 'delivered',
          payment_method: input.method,
          updated_at: new Date().toISOString(),
        })
        .eq('id', input.orderId)
    } else {
      // Check if all items are now paid → close order anyway
      const { data: remaining } = await supabase
        .from('order_items')
        .select('id')
        .eq('order_id', input.orderId)
        .is('paid_at', null)
      if (!remaining || remaining.length === 0) {
        await supabase
          .from('orders')
          .update({
            status: 'delivered',
            payment_method: input.method,
            updated_at: new Date().toISOString(),
          })
          .eq('id', input.orderId)
      }
    }

    return { success: true, payment: payment as PaymentRecord }
  } catch (err: any) {
    console.error('[NOIZA] processPayment error:', err)
    return { success: false, error: err?.message || 'Error al procesar pago' }
  }
}

// ─── Render printable invoice HTML and open in new window ─────────────────
export function openPrintableInvoice(args: {
  info: BillingInfo
  payment: PaymentRecord
  tableLabel?: string | null
}) {
  const { info, payment, tableLabel } = args
  const date = new Date(payment.created_at).toLocaleString('es-CR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
  const consecStr = String(payment.consecutive_number).padStart(6, '0')
  const methodLabel =
    payment.method === 'cash'
      ? 'Efectivo'
      : payment.method === 'sinpe'
        ? 'SINPE Móvil'
        : 'Tarjeta'

  const fmt = (n: number) =>
    new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
    }).format(n)

  const itemsHtml = (payment.items_json || [])
    .map(
      (it) => `
      <tr>
        <td class="qty">${it.quantity}×</td>
        <td class="name">${escapeHtml(it.item_name)}${it.notes ? `<div class="note">${escapeHtml(it.notes)}</div>` : ''}</td>
        <td class="price">${fmt(it.line_total)}</td>
      </tr>`,
    )
    .join('')

  const html = `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Factura #${consecStr} — ${escapeHtml(info.business_name)}</title>
  <style>
    @media print { @page { margin: 8mm; size: 80mm auto; } body { margin: 0; } .no-print { display:none !important; } }
    * { box-sizing: border-box; }
    body { font-family: 'Courier New', ui-monospace, monospace; color:#111; background:#f3f3f3; padding:24px; }
    .ticket { max-width: 320px; margin: 0 auto; background:#fff; padding:18px 16px; border:1px solid #e5e5e5; box-shadow: 0 4px 16px rgba(0,0,0,.06); }
    h1 { font-size: 16px; margin: 0 0 4px; text-align:center; letter-spacing: 1px; }
    .biz { text-align:center; font-size: 11px; line-height: 1.4; margin-bottom: 10px; }
    .biz strong { display:block; font-size:12px; }
    .meta { font-size: 11px; border-top: 1px dashed #999; border-bottom: 1px dashed #999; padding: 8px 0; margin: 8px 0; }
    .meta div { display:flex; justify-content: space-between; }
    table { width: 100%; border-collapse: collapse; margin: 6px 0; font-size: 11px; }
    td { padding: 3px 0; vertical-align: top; }
    td.qty { width: 28px; font-weight: 700; }
    td.name { padding-right: 6px; }
    td.price { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .note { font-size: 10px; color:#666; font-style: italic; }
    .totals { border-top: 1px dashed #999; padding-top: 8px; margin-top: 8px; font-size: 12px; }
    .totals .row { display:flex; justify-content:space-between; padding: 2px 0; }
    .totals .grand { font-size: 14px; font-weight: 800; border-top: 2px solid #111; margin-top: 4px; padding-top: 6px; }
    .pay { margin-top: 10px; padding: 6px 0; border-top: 1px dashed #999; font-size: 11px; display:flex; justify-content:space-between; }
    .footer { text-align:center; margin-top: 12px; font-size: 11px; color:#444; }
    .legal { text-align:center; margin-top: 8px; font-size: 9px; color:#666; line-height: 1.4; }
    .actions { max-width: 320px; margin: 12px auto 0; display:flex; gap: 8px; }
    .actions button { flex:1; padding: 10px; border: none; border-radius: 8px; font-weight:700; cursor:pointer; font-family: inherit; }
    .actions .print { background:#111; color:#fff; }
    .actions .close { background:#e5e5e5; color:#111; }
    .partial-badge { display:inline-block; background:#fef3c7; color:#92400e; font-size: 10px; padding: 2px 6px; border-radius: 4px; font-weight: 700; margin-left: 4px; }
  </style>
</head>
<body>
  <div class="ticket">
    <h1>${escapeHtml(info.business_name)}</h1>
    <div class="biz">
      <strong>Cédula ${escapeHtml(info.legal_id_type === 'fisica' ? 'Física' : 'Jurídica')}: ${escapeHtml(info.legal_id)}</strong>
      ${info.address ? `${escapeHtml(info.address)}<br/>` : ''}
      ${info.phone ? `Tel: ${escapeHtml(info.phone)}` : ''}
      ${info.email ? ` · ${escapeHtml(info.email)}` : ''}
    </div>

    <div class="meta">
      <div><span>Factura N°</span><strong>${consecStr}${payment.is_partial ? '<span class="partial-badge">PARCIAL</span>' : ''}</strong></div>
      <div><span>Fecha</span><span>${date}</span></div>
      ${tableLabel ? `<div><span>Mesa</span><span>${escapeHtml(tableLabel)}</span></div>` : ''}
    </div>

    <table>
      <thead>
        <tr style="border-bottom:1px solid #111; font-size:10px; text-transform:uppercase;">
          <td>Cant</td><td>Descripción</td><td style="text-align:right;">Total</td>
        </tr>
      </thead>
      <tbody>${itemsHtml}</tbody>
    </table>

    <div class="totals">
      <div class="row grand"><span>TOTAL</span><span>${fmt(payment.amount)}</span></div>
    </div>

    <div class="pay">
      <span>Forma de pago</span>
      <strong>${methodLabel}</strong>
    </div>

    ${info.footer_note ? `<div class="footer">${escapeHtml(info.footer_note)}</div>` : ''}
    <div class="legal">
      Régimen Tributario Simplificado<br/>
      Documento equivalente — no constituye factura electrónica
    </div>
  </div>

  <div class="actions no-print">
    <button class="close" onclick="window.close()">Cerrar</button>
    <button class="print" onclick="window.print()">Imprimir</button>
  </div>

  <script>setTimeout(() => window.print(), 350);</script>
</body>
</html>`

  const w = window.open('', '_blank', 'width=420,height=720')
  if (w) {
    w.document.write(html)
    w.document.close()
  }
}

function escapeHtml(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
